import React, { useState, useEffect, useRef } from 'react';
import Modal from '../Common/Modal';
import Collapse from '../Common/Collapse';
import SegToggle from '../Common/SegToggle';
import Dropdown from '../Common/Dropdown';
import { getStudentOverview, ensureStudentCode, grantExtraAttempt } from '../../services/adminService';
import { avatarColor, AvatarIcon } from '../../utils/avatar';
import { CoverPattern, shade } from './CourseCover';
import { CourseIcon } from './iconLibrary';
import EditStudentNameModal from './EditStudentNameModal';
import toast from 'react-hot-toast';

const MONO = "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace";
const IcLockSm = () => (<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="10.5" width="16" height="10.5" rx="2.4"/><path d="M8 10.5V7a4 4 0 0 1 8 0v3.5"/></svg>);
const MonthRu = ['ЯНВ','ФЕВ','МАР','АПР','МАЙ','ИЮН','ИЮЛ','АВГ','СЕН','ОКТ','НОЯ','ДЕК'];
const DaysRu  = ['Вс','Пн','Вт','Ср','Чт','Пт','Сб'];

const plural = (n, forms) => {
  const a = Math.abs(n) % 100, b = a % 10;
  if (a > 10 && a < 20) return forms[2];
  if (b > 1 && b < 5) return forms[1];
  if (b === 1) return forms[0];
  return forms[2];
};
const fmtDur = (mins) => {
  mins = Math.round(mins || 0);
  if (mins <= 0) return '';
  const h = Math.floor(mins / 60), m = mins % 60;
  return h > 0 ? `${h} ч${m ? ' ' + m + ' мин' : ''}` : `${m} мин`;
};
const formatDate = (d) => (d ? new Date(d).toLocaleDateString('ru-RU', { day: '2-digit', month: 'long', year: 'numeric' }) : '—');
const shortDate  = (d) => (d ? new Date(d).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—');
const relative = (d) => {
  if (!d) return 'не заходил';
  const a = new Date(d); a.setHours(0, 0, 0, 0);
  const b = new Date();  b.setHours(0, 0, 0, 0);
  const n = Math.round((b - a) / 86400000);
  if (n <= 0) return 'сегодня';
  if (n === 1) return 'вчера';
  if (n < 5) return `${n} дня назад`;
  if (n < 30) return `${n} дней назад`;
  return shortDate(d);
};

/* ── Иконки ─────────────────────────────────────────────────────────────── */
const IcWeb = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/>
  </svg>
);
const IcHw = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);
const IcVar = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/>
    <rect x="9" y="3" width="6" height="4" rx="2"/><line x1="9" y1="12" x2="15" y2="12"/><line x1="9" y1="16" x2="12" y2="16"/>
  </svg>
);
const IcChevron = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);
const IcCalendar = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--primary-dark)' }}>
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
  </svg>
);
const IcStar = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
  </svg>
);
const IcEye = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);
const IcReset = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 2v6h6"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L3 8"/>
  </svg>
);
const IcX = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const IcZoom = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="4"/>
  </svg>
);
const IcFire = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/>
  </svg>
);
const IcAchCheck = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
);
const IcInfo = () => (
  <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
  </svg>
);
// Выбор курса — тот же чип с меню, что у ученика в профиле
const IcChevDown = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);
const IcTick = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const IcBack = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
  </svg>
);

const STATUS_FG = { correct: '#16a34a', incorrect: '#dc2626', pending: '#d97706', not_submitted: 'var(--text-3)' };

/* Сводный статус ДЗ/варианта: принято / на проверке / не сдано. «Есть ошибки»
   отдельным статусом не считаем — сданное задание сдано, а сколько в нём верно,
   видно по шкале рядом. «На проверке» — когда остались номера для ручной проверки. */
const lessonStatus = (l) => {
  if (l.total === 0) return { key: 'empty', label: 'Нет номеров', fg: 'var(--text-3)' };
  if (!l.completed) return { key: 'todo', label: 'Не сдано', fg: 'var(--text-3)' };
  if (l.pending > 0) return { key: 'review', label: 'На проверке', fg: '#d97706' };
  return { key: 'done', label: 'Принято', fg: '#16a34a' };
};

/* ── Таблица ответов по номерам (раскрывается под строкой ДЗ/варианта) ──── */
function AnswersPanel({ tasks }) {
  if (!tasks || tasks.length === 0) {
    return <div className="spm-ans"><div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>У этого задания нет номеров</div></div>;
  }
  return (
    <div className="spm-ans">
      <div className="spm-ans-legend">
        <span className="spm-cap">Ответы по номерам</span>
      </div>
      <div className="spm-nums">
        {tasks.map(t => (
          <span key={t.id} className="spm-num" title={t.status === 'not_submitted' ? 'без ответа' : t.status === 'correct' ? 'верно' : t.status === 'incorrect' ? 'ошибка' : 'на проверке'}
            style={{ borderBottomColor: t.status === 'not_submitted' ? 'var(--border)' : STATUS_FG[t.status] }}>
            №{t.n}
          </span>
        ))}
      </div>
      <div className="spm-table">
        <div className="spm-tr spm-th">
          <span>Номер</span><span>Ответ ученика</span><span>Верный ответ</span><span>Балл</span>
        </div>
        {tasks.map(t => (
          <div className="spm-tr" key={t.id}>
            <span style={{ fontFamily: MONO, fontWeight: 700, color: 'var(--text-2)' }}>№{t.n}</span>
            <span style={{ fontFamily: MONO, fontWeight: 600, color: t.status === 'not_submitted' ? 'var(--text-3)' : STATUS_FG[t.status], overflowWrap: 'anywhere' }}>{t.answer || '—'}</span>
            <span style={{ fontFamily: MONO, fontWeight: 600, color: 'var(--text-2)', overflowWrap: 'anywhere' }}>{t.answerHidden ? 'после сдачи' : (t.correctAnswer || 'ручная проверка')}</span>
            <span style={{ fontWeight: 700, color: 'var(--text-1)', fontVariantNumeric: 'tabular-nums' }}>{t.score} / {t.maxScore}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Карточка модуля (визуал как у ученика: .mod). Отметок «текущего» модуля
      здесь нет вовсе — в карточке ученика важен только его прогресс. ─────── */
function ModuleCard({ mod, icon, sub, progress, children, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  const card = (
    <div className={`mod${open ? ' open' : ''}`}>
      <div className="mod-head" onClick={() => setOpen(o => !o)}>
        <div className="mod-ic">{icon}</div>
        <div className="mod-info">
          <div className="mod-title">{mod.name}</div>
          <div className="mod-sub">{sub}</div>
        </div>
        {progress && (
          <div className="mod-prog">
            <div className="mod-prog-top"><span>{progress.label}</span><b>{progress.pct}%</b></div>
            <div className="mod-prog-bar"><span style={{ width: `${progress.pct}%` }} /></div>
          </div>
        )}
        <span className="mod-chev"><IcChevron /></span>
      </div>
      <Collapse open={open} keepMounted>
        <div className="mod-body">{children}</div>
      </Collapse>
    </div>
  );
  return card;
}

/* ── Вебинары: таймлайн с кружками, как в личном кабинете ученика ───────── */
function WebinarsTab({ modules, stats, loading }) {
  return (
    <div>
      <PerfCard stats={stats} loading={loading} only="web" />
      {!modules.length && <div className="spm-empty">У этого ученика пока нет вебинаров</div>}
      {modules.map((mod, i) => {
        const n = mod.lessons.length;
        const viewed = mod.lessons.filter(l => l.status === 'viewed' || l.status === 'completed').length;
        const totalMin = mod.lessons.reduce((s, l) => s + (l.duration || 0), 0);
        return (
          <ModuleCard
            key={mod.id} mod={mod} icon={<IcWeb />}
            sub={`${n} ${plural(n, ['вебинар', 'вебинара', 'вебинаров'])}${totalMin > 0 ? ` · ${fmtDur(totalMin)}` : ''}`}
            progress={{ label: `Просмотрено ${viewed} / ${n}`, pct: n ? Math.round((viewed / n) * 100) : 0 }}
          >
            <div className="tline">
              {mod.lessons.map(l => {
                const d = l.date ? new Date(l.date) : null;
                const isDone = l.status === 'completed';
                const isViewed = !isDone && l.status === 'viewed';
                const itemCls = isDone ? ' done' : (isViewed ? ' viewed' : '');
                return (
                  <div key={l.id} className={`titem${itemCls}${l.locked ? ' locked' : ''}`}>
                    <div className={`ccard${l.locked ? ' locked' : ''}`} style={{ cursor: 'default' }}>
                      {/* Плашка даты серая всегда — как в «Вебинарах» у ученика */}
                      <div className="dbadge past">
                        {d ? (<><span className="d">{d.getDate()}</span><span className="m">{MonthRu[d.getMonth()]}</span></>) : <IcCalendar />}
                      </div>
                      <div className="linfo">
                        <div className="lname">{l.title}</div>
                        <div className="lmeta">
                          {d && <span><b>{DaysRu[d.getDay()]}</b>{l.time ? `, ${String(l.time).slice(0, 5)}` : ''}</span>}
                          {l.duration > 0 && <span>· {fmtDur(l.duration)}</span>}
                          {l.hwCount > 0 && <span>· ДЗ {l.hwCount} {plural(l.hwCount, ['номер', 'номера', 'номеров'])}</span>}
                          {l.joinedAt && <span>· вход в эфир {shortDate(l.joinedAt)}</span>}
                        </div>
                      </div>
                      <div className="lright">
                        {/* Пробнику занятие закрыто — показываем это и админу,
                            чтобы карточка совпадала с тем, что видит ученик */}
                        {l.locked ? <span className="lock-chip" title="Нет в пробном доступе"><IcLockSm /></span> : <>
                          {isDone && <span className="pg-chip green">Пройден</span>}
                          {isViewed && <span className="pg-chip amber">Просмотрен</span>}
                          {!isDone && !isViewed && <span className="pg-chip" style={{ background: 'var(--bg)', color: 'var(--text-3)' }}>Не открывал</span>}
                        </>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </ModuleCard>
        );
      })}
    </div>
  );
}

/* ── Строка ДЗ / варианта: дата-иконка со статусным свечением + разбор ──── */
function WorkRow({ item, kind, studentId, onReset, readOnly }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const st = lessonStatus(item);
  const d = item.date ? new Date(item.date) : null;
  const state = st.key === 'done' ? 'done' : st.key === 'review' ? 'started' : 'todo';
  const correctPct = item.total ? Math.round((item.correct / item.total) * 100) : 0;

  // «Ещё попытку» ничего не сбрасывает: задание остаётся сданным с прежним
  // баллом, а ученик может пройти его снова — засчитается лучший результат.
  const grant = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      await grantExtraAttempt(studentId, item.id);
      toast.success('Попытка добавлена — засчитается лучший результат');
      onReset?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Не удалось добавить попытку');
    } finally { setBusy(false); }
  };

  return (
    <div>
      {/* Раскрыть можно всегда — смотреть ответы имеет право любой сотрудник.
          Кнопка сброса внутри уже закрыта правами. */}
      <div className={`hwc ${state}${item.locked ? ' locked' : ''}`} onClick={() => setOpen(o => !o)} style={{ cursor: 'pointer' }}>
        <div className="hwc-date" data-state={state}>
          {d ? (<><b>{d.getDate()}</b><i>{MonthRu[d.getMonth()]}</i></>) : <IcStar />}
        </div>

        <div className="hwc-main">
          <div className="hwc-title">{item.title}</div>
          <div className="hwc-sub">
            {item.total} {plural(item.total, ['номер', 'номера', 'номеров'])}
            {kind === 'var' && item.maxPrimary > 0 && ` · ${item.primary} из ${item.maxPrimary} первичных`}
            {kind === 'var' && item.examType === 'ЕГЭ' && item.secondary != null && ` → ${item.secondary} тестовых`}
            {kind === 'var' && item.examType === 'ОГЭ' && item.grade != null && ` → оценка ${item.grade}`}
          </div>
          {(item.retryActive || item.extraAttempts > 0) && (
            <div className="spm-attempt">
              {item.retryActive ? 'Проходит повторную попытку'
                : item.extraAttempts === 1 ? 'Доступна ещё одна попытка' : `Доступно ещё попыток: ${item.extraAttempts}`}
            </div>
          )}
        </div>

        <div className="hwc-prog">
          <span className="hwc-cnt"><b>{item.correct} / {item.total}</b> верно</span>
          <span className="hwc-bar"><span style={{ width: `${correctPct}%` }} /></span>
        </div>

        {item.locked
          ? <span className="lock-chip" title="Нет в пробном доступе"><IcLockSm /></span>
          : (
            <span className="pg-chip spm-st" style={{ background: 'transparent', color: st.fg, fontWeight: 700, flexShrink: 0 }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: st.fg }} />{st.label}
            </span>
          )}

        {/* Родителю на публичной странице показываем только результат */}
        {!readOnly && (
          <div className="spm-row-actions">
            <button type="button" className="spm-mini" onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}>
              <IcEye />{open ? 'Скрыть' : 'Ответы'}
            </button>
            <button type="button" className="spm-mini"
              title={item.completed ? 'Дать ещё одну попытку — засчитается лучший результат' : 'Ученик ещё не сдал — попытка у него и так открыта'}
              onClick={grant} disabled={busy || !item.completed}>
              <IcReset />Ещё попытку
            </button>
          </div>
        )}
      </div>
      <Collapse open={open}><AnswersPanel tasks={item.tasks} /></Collapse>
    </div>
  );
}

function HomeworkTab({ modules, studentId, onReset, stats, loading, readOnly }) {
  return (
    <div>
      <PerfCard stats={stats} loading={loading} only="hw" />
      {!modules.length && <div className="spm-empty">К курсу ученика пока не привязано ни одного ДЗ</div>}
      {modules.map((mod, i) => {
        const n = mod.lessons.length;
        // Считаем выполненные ЗАНЯТИЯ — так же, как в «Прогрессе» у самого ученика
        const done = mod.lessons.filter(l => l.completed).length;
        return (
          <ModuleCard
            key={mod.id} mod={mod} icon={<IcHw />}
            sub={`${n} ${plural(n, ['задание', 'задания', 'заданий'])}`}
            progress={{ label: `Выполнено ${done} / ${n}`, pct: n ? Math.round((done / n) * 100) : 0 }}
          >
            <div className="hw-list">
              {mod.lessons.map(l => <WorkRow key={l.id} item={l} kind="hw" studentId={studentId} onReset={onReset} readOnly={readOnly} />)}
            </div>
          </ModuleCard>
        );
      })}
    </div>
  );
}

function VariantsTab({ variants, studentId, onReset, stats, loading, readOnly }) {
  // Варианты не разложены по модулям — показываем их одной группой в том же оформлении
  const done = variants.filter(v => v.completed).length;
  const mod = { id: 'variants', name: 'Пробные варианты', isCurrent: false };
  return (
    <div>
    <PerfCard stats={stats} loading={loading} only="var" />
    {!variants.length ? <div className="spm-empty">В курсе ученика пока нет вариантов</div> : (
    <ModuleCard
      mod={mod} icon={<IcVar />}
      sub={`${variants.length} ${plural(variants.length, ['вариант', 'варианта', 'вариантов'])}`}
      progress={{ label: `Решено ${done} / ${variants.length}`, pct: variants.length ? Math.round((done / variants.length) * 100) : 0 }}
    >
      <div className="hw-list">
        {variants.map(v => <WorkRow key={v.id} item={v} kind="var" studentId={studentId} onReset={onReset} readOnly={readOnly} />)}
      </div>
    </ModuleCard>
    )}
    </div>
  );
}


// «Осталось» — песочные часы: что ещё предстоит пройти
const IcLeft = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2h12M6 22h12M8 2v4a4 4 0 0 0 4 4 4 4 0 0 0 4-4V2M8 22v-4a4 4 0 0 1 4-4 4 4 0 0 1 4 4v4"/></svg>);

const TABS = [
  { key: 'web',      label: 'Вебинары' },
  { key: 'hw',       label: 'Домашние задания' },
  { key: 'var',      label: 'Варианты' },
  { key: 'activity', label: 'Недавняя активность' },
];

/* ── Карточка курса с обложкой: тот же визуал, что на главной админки,
      но в подвале — общий прогресс ученика и переход к подробностям ──── */
function CourseCoverCard({ c, onOpen, onRemove, onAll, pickRef, menu }) {
  const pct = c.progress || 0;
  const level = c.examType || 'Курс';
  return (
    <div className="spm-crs">
      <div className="spm-crs-cover">
        <CoverPattern id={c.id} />
        <div className="spm-crs-top">
          <span className="spm-crs-badge">
            <i style={{ background: c.color || '#7c4dff' }} />{level}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {onRemove && (
              <button type="button" className="spm-crs-x" title="Убрать с курса" onClick={onRemove}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            )}
            <span className="spm-crs-ic"><CourseIcon icon={c.icon || 'zap'} size={17} sw={2} /></span>
          </span>
        </div>
        <div className="spm-crs-title">{c.name}</div>
      </div>

      <div className="spm-crs-foot">
        <div className="spm-crs-line">
          <span className="spm-cap">Общий прогресс</span>
          <span className="spm-crs-pct">{pct}<i>%</i></span>
        </div>
        <div className="spm-bar"><div style={{ width: `${pct}%` }} /></div>
        <div className={`spm-crs-line bottom${onAll ? ' two' : ''}`} ref={pickRef}>
          {/* Слева — выбор курса (только если курсов несколько), справа — переход к подробностям */}
          {onAll && (
            <button type="button" className="spm-crs-all" onClick={onAll} title="Выбрать курс">
              Все курсы <IcChevDown />
            </button>
          )}
          <button type="button" className="spm-crs-btn" onClick={onOpen}>
            Посмотреть <span className="spm-crs-chev"><IcChevron /></span>
          </button>
          {menu}
        </div>
      </div>
    </div>
  );
}

/* ── «Успеваемость по курсу»: метрики плитками.
      only='web'|'hw'|'var' — одна плитка-шапка над модулями вкладки. ─────── */
function PerfCard({ stats, loading, only, onPick }) {
  const cells = [
    // Вебинары считаем как «открытые» (просмотрен или завершён) — ровно то же,
    // что показывают шкалы модулей под этой карточкой
    { key: 'web', label: 'Вебинары', icon: <IcWeb />, color: 'var(--text-1)',
      done: stats.lessonsOpened || 0, total: stats.lessonsTotal || 0 },
    { key: 'hw',  label: 'Домашние задания', short: 'ДЗ', icon: <IcHw />, color: 'var(--stu-green)', done: stats.hwDone || 0, total: stats.hwTotal || 0 },
    { key: 'var', label: 'Варианты', icon: <IcVar />, color: 'var(--stu-green)', done: stats.variantsDone || 0, total: stats.variantsTotal || 0 },
  ].filter(c => !only || c.key === only);

  return (
    <div className={`spm-perf${only ? ' one' : ''}`}>
      {!only && <div className="spm-perf-head"><span className="spm-cap">Успеваемость по курсу</span></div>}
      <div className="spm-perf-grid">
        {cells.map(c => {
          const pct = c.total ? Math.round((c.done / c.total) * 100) : 0;
          const Tag = onPick ? 'button' : 'div';
          return (
            <Tag key={c.key} type={onPick ? 'button' : undefined}
              className={`spm-perf-cell${onPick ? ' link' : ''}`} style={{ '--pc': c.color }}
              onClick={onPick ? () => onPick(c.key) : undefined}
              title={onPick ? `Открыть раздел «${c.label}»` : undefined}>
              <div className="spm-perf-top">
                <span className="spm-perf-ic">{c.icon}</span>
                {/* На узком экране длинная подпись съедает шкалу — там короткая */}
                <span className="spm-perf-lbl">{c.label}</span>
                <span className="spm-perf-lbl sm">{c.short || c.label}</span>
                {onPick && <span className="spm-perf-go"><IcChevron /></span>}
              </div>
              <div className="spm-perf-num">
                {loading ? '·' : c.done}<i>/{loading ? '·' : c.total}</i>
                {/* в шапке вкладки процент лишний — там и так одна метрика */}
                {!only && <b>{loading ? '' : `${pct}%`}</b>}
              </div>
              <div className="spm-perf-bar"><div style={{ width: `${loading ? 0 : pct}%` }} /></div>
            </Tag>
          );
        })}
      </div>
    </div>
  );
}

/* ── «Недавняя активность»: тот же дизайн строки, что у ученика в «Прогрессе» ── */
const ACT_KIND = { 'ВЕБ': 'web', 'ДЗ': 'hw', 'ВАР': 'variant' };
const ACT_LABEL = { web: 'вебинар', hw: 'дз', variant: 'вариант' };
// Метка действия — те же чипы, что метки «Просмотрен»/«Пройден» у ученика
// в «Вебинарах»: зелёный на сделанное, янтарный на промежуточное, красный на ошибку.
const ACT_TONE = { good: 'green', bad: 'red', warn: 'amber', muted: 'amber' };
// Плашка иконки одинаковая для всех типов — различает их сам значок, а не цвет
const ACT_IC = { bg: 'var(--primary-light)', fg: 'var(--primary-dark)', shadow: 'none' };
const actSvg = (k, s) => (k === 'hw' ? <IcHw /> : k === 'variant' ? <IcVar /> : <IcWeb />);

function ActivityTab({ activity }) {
  return (
    <div className="spm-actwrap">
      <div className="spm-cap" style={{ marginBottom: 14 }}>Недавняя активность</div>
      {activity.length === 0 ? (
        <div style={{ color: 'var(--text-3)', fontSize: 13, padding: '10px 0' }}>Активности пока нет</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          {activity.map((a, i) => {
            const kind = ACT_KIND[a.kind] || 'web';
            const ic = ACT_IC;
            return (
              <div key={i} className="pg2-act spm-act">
                <span className="pg-actic spm-act-ic" style={{ background: ic.bg, color: ic.fg, boxShadow: ic.shadow }}>{actSvg(kind)}</span>
                <div className="spm-act-main">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
                    <span className="spm-act-t">{a.title}</span>
                    <span className="spm-act-k">{ACT_LABEL[kind]}</span>
                  </div>
                  <div className="spm-act-m">{a.module}</div>
                </div>
                <span className={`pg-chip ${ACT_TONE[a.tone] || 'amber'}`}>{a.action}</span>
                <span className="spm-act-when">{relative(a.at)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


export default function StudentProfileModal({ student, isOpen, onClose, onAddCourse, onRemoveCourse, onEditZoom, onDeactivate, onActivate, onDelete, onUpdated, overview, readOnly, canCode = true }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('web');
  const [detail, setDetail] = useState(false); // false — обложка курса + «Данные», true — подробности
  const [code, setCode] = useState('');
  const [copied, setCopied] = useState(false); // код только что скопирован
  const [pwShown, setPwShown] = useState(false); // временный пароль показан (скрывается при новом открытии)
  const [courseId, setCourseId] = useState(null); // какой курс показываем (у ученика их может быть несколько)
  const [pickOpen, setPickOpen] = useState(false); // меню выбора курса
  const pickRef = useRef(null);
  const [nameEdit, setNameEdit] = useState(false); // модалка «Редактировать профиль»

  // На публичной странице данные приходят готовыми (по коду ученика) —
  // админский эндпоинт оттуда недоступен, да и не нужен.
  const load = React.useCallback(() => {
    if (!student || overview) return;
    setLoading(true);
    getStudentOverview(student.id)
      .then(res => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [student, overview]);

  useEffect(() => {
    if (!isOpen || !student) return;
    setTab('web');
    setDetail(false);
    setCopied(false);
    setPwShown(false);
    setCode(student.progressCode || '');
    setCourseId(null);            // при открытии — первый курс ученика
    setPickOpen(false);
    setNameEdit(false);
    if (overview) { setData(overview); setLoading(false); return; }
    setData(null);
    load();
  }, [isOpen, student, overview, load]);

  useEffect(() => {
    if (!pickOpen) return undefined;
    const onDoc = (e) => { if (!pickRef.current?.contains(e.target)) setPickOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [pickOpen]);

  if (!student) return null;

  const stats = data?.stats || {};

  /* Карточка показывает ОДИН курс: обложка, успеваемость, достижения и
     подробности — всё по выбранному. Переключатель появляется от двух курсов.
     Запасной вариант (первый курс) закрывает случай, когда курс уже убрали. */
  const courses = data?.courses || [];
  const current = courses.find(c => c.id === courseId) || courses[0] || null;
  const inCourse = (x) => !current || x.courseId === current.id;
  const webinars = (data?.webinars || []).filter(inCourse);
  const homeworks = (data?.homeworks || []).filter(inCourse);
  const variants = (data?.variants || []).filter(inCourse);
  // У старых записей ленты курса нет — их не прячем
  const activity = (data?.activity || []).filter(a => !current || a.courseId == null || a.courseId === current.id);
  // Цифры по выбранному курсу считает сервер (courses[].webDone/hwDone/varDone)
  const cstats = current ? {
    ...stats,
    lessonsOpened: current.webDone || 0, lessonsTotal: current.webTotal || 0,
    hwDone: current.hwDone || 0, hwTotal: current.hwTotal || 0,
    variantsDone: current.varDone || 0, variantsTotal: current.varTotal || 0,
  } : stats;

  /* Достижения — ровно по плиткам «Вебинары · ДЗ · Варианты» ниже:
     пройдено = сумма «сделано» (вебинар засчитан, как только просмотрен),
     осталось = сумма «всего» минус пройдено.
     «Лучший пробник» — максимум по завершённым вариантам: ЕГЭ — вторичный балл,
     ОГЭ — оценка. */
  const achDoneN = data
    ? (cstats.lessonsOpened || 0) + (cstats.hwDone || 0) + (cstats.variantsDone || 0)
    : null;
  const achLeftN = data
    ? Math.max(0, (cstats.lessonsTotal || 0) + (cstats.hwTotal || 0) + (cstats.variantsTotal || 0) - achDoneN)
    : null;
  const achBest = (() => {
    const done = variants.filter(v => v.completed);
    const ege = done.map(v => v.secondary).filter(x => typeof x === 'number');
    if (ege.length) { const b = Math.max(...ege); return `${b} ${plural(b, ['балл', 'балла', 'баллов'])}`; }
    const oge = done.map(v => v.grade).filter(x => typeof x === 'number');
    if (oge.length) return `Оценка ${Math.max(...oge)}`;
    return '—';
  })();
  // Ведём себя как чип кода в списке учеников: плашка зеленеет + тост
  const copy = async () => {
    let value = code;
    if (!value && !canCode) return;   // кода ещё нет, а выдавать его этой роли нельзя
    if (!value) {
      try {
        const res = await ensureStudentCode(student.id);
        value = res?.data?.code || '';
        setCode(value);
      } catch { toast.error('Не удалось создать код'); return; }
    }
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true); setTimeout(() => setCopied(false), 1400);
      toast.success(`Код ${value} скопирован`);
    } catch { toast.error('Буфер обмена недоступен'); }
  };

  // Временный пароль, выданный при добавлении: пропадает, как только ученик
  // задал свой (бэкенд обнуляет поле).
  const tempPassword = data?.student?.generatedPassword || student.generatedPassword || null;
  const copyPassword = async () => {
    if (!tempPassword) return;
    setPwShown(true);
    try {
      await navigator.clipboard.writeText(tempPassword);
      toast.success('Пароль скопирован');
    } catch { toast.error('Буфер обмена недоступен'); }
  };

  // «Был(а) в сети» — из карточки, иначе из строки списка
  const lastSeen = stats.lastActiveAt || student.lastActiveAt || null;

  // Карточка «Данные» — те же поля и вёрстка, что ученик видит у себя в профиле
  const rows = [
    ['Имя', student.name || '—'],
    ['Email', student.email || '—'],
    ['Курс', (() => {
      const names = courses.length
        ? courses.map(c => c.name)
        : (student.StudentCourses || []).map(sc => sc.Course?.name).filter(Boolean);
      if (!names.length) return 'Не назначен';
      // Несколько курсов — каждый со своей строки, а не через запятую
      return <span className="spm-v-lines">{names.map((n, i) => <span key={i}>{n}</span>)}</span>;
    })()],
    ['Регистрация', formatDate(student.created_at)],
    ['Цель по баллам', data?.student?.goalScore || student.goalScore || '—'],
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} bare>
      <div className={`adm-modal w-student${detail ? ' detail' : ''}`} onClick={e => e.stopPropagation()}>

        {/* ── Шапка ── */}
        <div className="spm-head">
          <div className="spm-ava" style={{ background: avatarColor(student.id) }}>
            {student.avatar
              ? <img src={student.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <AvatarIcon id={student.id} size={29} />}
          </div>
          <div className="spm-idcol">
            <div className="spm-nameline">
              <div className="spm-name">{student.name || 'Без имени'}</div>
              {student.isTrial && <span className="stu-code stu-code-trial spm-trial">Пробный</span>}
              {!readOnly && (
                <button type="button" className="spm-nm-edit" onClick={() => setNameEdit(true)} title="Изменить имя и фамилию">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
                  </svg>
                </button>
              )}
            </div>
            <div className="spm-sub">
              <span style={{ color: 'var(--text-3)' }}>{student.email}</span>
              <span className="spm-seen"><i />был(а) в сети {relative(lastSeen || student.created_at)}</span>
              {student.isActive === false && <span className="spm-off">Отключён</span>}
            </div>
          </div>

          {/* Временный пароль — только пока ученик не задал свой */}
          {tempPassword && (
            <div className="spm-code-box pw">
              <div className="spm-cap">Сгенерированный пароль</div>
              <button type="button" className={`stu-code spm-pw${pwShown ? ' shown' : ''}`}
                onClick={copyPassword}
                title={pwShown ? 'Скопировать пароль' : 'Показать и скопировать пароль'}>
                {pwShown ? tempPassword : '••••••••'}
                <svg className="stu-code-ic" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                </svg>
              </button>
            </div>
          )}

          {/* Zoom и код ученика — одна линия сверху (подписи) и снизу (плашки) */}
          {onEditZoom && (() => {
            // Зелёная, если ссылка есть: выданная Zoom по курсу или вписанная руками
            const autoLinks = (student.StudentCourses || []).filter(sc => sc.zoomJoinUrl);
            const hasLink = !!student.zoomLink || autoLinks.length > 0;
            return (
              <div className="spm-code-box">
                <div className="spm-cap">Zoom</div>
                <button type="button" className={`spm-zoom${hasLink ? ' on' : ''}`}
                  onClick={() => onEditZoom(student)}
                  title={hasLink
                    ? `Ссылка на вебинары есть${autoLinks.length ? ` (${autoLinks.length} по курсам)` : ''} — посмотреть`
                    : 'Ссылки на вебинары нет — добавить'}>
                  <IcZoom />
                </button>
              </div>
            );
          })()}

          <div className="spm-code-box">
            <div className="spm-cap">Код ученика</div>
            <button type="button" className="stu-code spm-code2" onClick={copy}
              data-ok={copied ? 'true' : undefined} title="Скопировать код">
              {code || '····'}
              <svg className="stu-code-ic" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {copied
                  ? <polyline points="20 6 9 17 4 12" />
                  : <><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></>}
              </svg>
            </button>
          </div>
          {onClose && <button type="button" className="adm-x" onClick={onClose} aria-label="Закрыть"><IcX /></button>}
        </div>

        {/* ── Тело ── */}
        <div className="adm-modal-body spm-body">
          {!detail ? (
            /* ── Экран 1: обложка курса, «Данные», «Достижения» + успеваемость ── */
            <>
              <div className="spm-cards3">
                <div className="spm-crs-col">
                  {current ? (
                    <CourseCoverCard
                      key={current.id} c={current}
                      onOpen={() => setDetail(true)}
                      onRemove={onRemoveCourse ? () => onRemoveCourse(student, current.id, current.name) : null}
                      onAll={courses.length > 1 ? () => setPickOpen(o => !o) : null}
                      pickRef={pickRef}
                      menu={pickOpen && courses.length > 1 ? (
                        <div className="prof-course-menu spm-crs-menu">
                          <div className="pcm-title">Курсы ученика</div>
                          <div className="pcm-list">
                            {courses.map(c => {
                              const col = c.color || '#7c4dff';
                              const on = c.id === current.id;
                              return (
                                <button key={c.id} type="button" className={`pcm-card${on ? ' on' : ''}`}
                                  style={{ '--st-t-light': `${col}80`, '--st-t-dark': `${col}38`, '--st-chip-fg': shade(col, -42), '--st-chip-fg-dark': shade(col, 55), '--st-c': col }}
                                  onClick={() => { setCourseId(c.id); setPickOpen(false); }}>
                                  <span className="st-crs-pat"><CoverPattern id={`pcm${c.id}`} /></span>
                                  <span className="pcm-card-top">
                                    {c.examType ? <span className="st-crs-lvl"><i />{c.examType}</span> : <span />}
                                    <span className="pcm-card-r">
                                      {on && <span className="pcm-card-on"><IcTick /></span>}
                                      <span className="st-crs-ic"><CourseIcon icon={c.icon} size={14} sw={2} /></span>
                                    </span>
                                  </span>
                                  <span className="pcm-card-name">{c.name}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ) : null}
                    />
                  ) : (
                    <div className="spm-crs empty">
                      <div className="spm-crs-cover">
                        <CoverPattern id={`none-${student.id}`} />
                        <div className="spm-crs-title" style={{ opacity: .75 }}>Без курса</div>
                      </div>
                      <div className="spm-crs-foot">
                        <div className="spm-crs-sub" style={{ marginBottom: 12 }}>Ученик пока не зачислен ни на один курс</div>
                        {onAddCourse
                          ? <button type="button" className="spm-crs-btn solo" onClick={() => onAddCourse(student)}>Добавить курс</button>
                          : <button type="button" className="spm-crs-btn solo" onClick={() => setDetail(true)}>Посмотреть</button>}
                      </div>
                    </div>
                  )}
                </div>

                <div className="spm-card">
                  <div className="spm-cap">Данные</div>
                  <div className="prof-rows" style={{ marginTop: 4 }}>
                    {rows.map(([k, v]) => (
                      <div key={k} className="prof-row"><span className="prof-k">{k}</span><span className="prof-v">{v}</span></div>
                    ))}
                  </div>
                </div>

                {/* Достижения — пока демо, как в профиле у ученика */}
                <div className="spm-card">
                  <div className="spm-cap">Достижения</div>
                  <div className="ach big" style={{ marginTop: 16 }}>
                    <div className="ach-item"><div className="ach-ic fire"><IcFire /></div><div><b>12 дней<span className="demo-tag"><IcInfo />демо</span></b><span>без пропусков</span></div></div>
                    <div className="ach-item"><div className="ach-ic"><IcAchCheck /></div><div><b>{achDoneN == null ? '—' : `${achDoneN} ${plural(achDoneN, ['урок', 'урока', 'уроков'])}`}</b><span>пройдено</span></div></div>
                    <div className="ach-item"><div className="ach-ic"><IcVar /></div><div><b>{achBest}</b><span>лучший пробник</span></div></div>
                    <div className="ach-item"><div className="ach-ic"><IcLeft /></div><div><b>{achLeftN == null ? '—' : `${achLeftN} ${plural(achLeftN, ['урок', 'урока', 'уроков'])}`}</b><span>осталось</span></div></div>
                  </div>
                </div>
              </div>

              <PerfCard stats={cstats} loading={loading} onPick={(k) => { setTab(k); setDetail(true); }} />
            </>
          ) : (
            /* ── Экран 2: тумблер сверху, под ним подробности фиксированной высоты ── */
            <>
              <div className="spm-tabs">
                <button type="button" className="spm-back" onClick={() => setDetail(false)} title="К карточке курса">
                  <IcBack />
                </button>
                <SegToggle value={tab} onChange={setTab} options={TABS} />
                {/* На узком экране четыре сегмента не помещаются — там же,
                    но выпадающим списком с названием текущего раздела. */}
                <div className="spm-tabs-sel">
                  <Dropdown value={tab} onChange={setTab}
                    options={TABS.map(t => ({ value: t.key, label: t.label }))} />
                </div>
              </div>

              <div className="spm-detail">
                {loading ? (
                  <div className="spm-empty">Загружаем данные ученика…</div>
                ) : !data ? (
                  <div className="spm-empty">Не удалось загрузить данные ученика</div>
                ) : tab === 'activity' ? (
                  <ActivityTab activity={activity} />
                ) : tab === 'web' ? (
                  <WebinarsTab modules={webinars} stats={cstats} loading={loading} />
                ) : tab === 'hw' ? (
                  <HomeworkTab modules={homeworks} studentId={student.id} onReset={load} stats={cstats} loading={loading} readOnly={readOnly} />
                ) : (
                  <VariantsTab variants={variants} studentId={student.id} onReset={load} stats={cstats} loading={loading} readOnly={readOnly} />
                )}
              </div>
            </>
          )}
        </div>

        {/* ── Подвал ── */}
        {(onAddCourse || onActivate || onDeactivate || onDelete) && (
          <div className="adm-modal-foot">
            {onAddCourse && <button className="spm-foot-btn" onClick={() => onAddCourse(student)}>+ Добавить курс</button>}
            {student.isActive === false
              ? onActivate && <button className="spm-foot-btn ok" onClick={() => { onActivate(student); onClose(); }}>Активировать</button>
              : onDeactivate && <button className="spm-foot-btn danger" onClick={() => { onDeactivate(student); onClose(); }}>Перенести в архив</button>}
            {/* Без архива: аккаунт и все данные ученика стираются */}
            {onDelete && <button className="spm-foot-btn danger" onClick={() => { onDelete(student); onClose(); }}>Удалить навсегда</button>}
          </div>
        )}
      </div>

      {/* Правка имени — отдельной модалкой поверх карточки */}
      <EditStudentNameModal
        isOpen={nameEdit}
        onClose={() => setNameEdit(false)}
        student={student}
        onSaved={() => { onUpdated?.(); load(); }}
      />
    </Modal>
  );
}
